import { useEffect, useMemo } from 'react';
import { debounce } from 'lodash';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import { indexWorkflows, workflowQueryKeys } from 'services/api/workflows';

const NUDGE_DEBOUNCE_MS = 400;
const NUDGE_MAX_WAIT_MS = 1500;

// The fallback cadence only (OLIO-2602): the WorkflowListChannel nudge is the primary
// freshness signal; this slow poll covers dropped sockets and missed events.
const FALLBACK_POLL_INTERVAL_MS = 60_000;

/**
 * The list data hook for the Inbound Referrals inbox. Fetching lives here so the
 * page is agnostic to how fresh data arrives.
 */
export default function useWorkflows() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: workflowQueryKeys.list,
    queryFn: ({ signal }) => indexWorkflows(signal),
    refetchInterval: FALLBACK_POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });

  // Live updates (OLIO-2602): one nudge per settled run in this client — refetch the
  // list through the same query so visibility and shape stay the fetch's business.
  // Debounced: several runs settling together (or one busy run) collapse into one
  // list refetch; the max wait bounds staleness under a steady stream.
  const nudged = useMemo(
    () =>
      debounce(() => queryClient.invalidateQueries({ queryKey: workflowQueryKeys.list }), NUDGE_DEBOUNCE_MS, {
        maxWait: NUDGE_MAX_WAIT_MS,
      }),
    [queryClient]
  );
  useEffect(() => () => nudged.cancel(), [nudged]);

  useWebsocketSubscription({
    channel: 'WorkflowListChannel',
    onReceived: () => nudged(),
  });

  return {
    workflows: query.data ?? [],
    isLoading: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  };
}
